import { getAccessTokenCookie } from "./cookies";
import { getEnv } from "./env";
import { getRequestAuthToken } from "./requestAuth";
import { authDebug, authDebugError } from "./safeAuthLog";
import { logoutUser, refreshTokenIfNeeded } from "./tokenManager";

export type ApiError = Error & { status?: number; detail?: string };

// Extract FastAPI's `detail` field from a non-OK response so callers can show
// the backend's actual error message instead of a generic "request failed".
async function buildApiError(response: Response): Promise<ApiError> {
  let detail: string | undefined;
  try {
    const body = await response.clone().json();
    const raw = body?.detail;
    if (typeof raw === "string") {
      detail = raw;
    } else if (raw != null) {
      detail = JSON.stringify(raw);
    }
  } catch {
    // Body wasn't JSON (HTML error page, empty body, etc.) — leave detail undefined.
  }

  const message = detail
    ? `API call failed with status ${response.status}: ${detail}`
    : `API call failed with status ${response.status}: ${response.statusText}`;
  const error = new Error(message) as ApiError;
  error.status = response.status;
  error.detail = detail;
  return error;
}

/**
 * Simplified API call function that always gets fresh tokens from MSAL
 * This eliminates token synchronization issues when multiple users share an account
 */
export async function callApi(
  apiUri: string,
  method: string,
  queryParams: Record<string, string> | null = null,
  bodyParams: FormData | Record<string, unknown> | null = null
) {
  // Normalise the base URL: tolerate VITE_BACKEND_URL with or without a
  // trailing slash. Front Door origins are configured without one (e.g.
  // https://…azurefd.net), which otherwise concatenates to "…netapi/v1/…".
  const baseAPIUrl = getEnv("VITE_BACKEND_URL").replace(/\/+$/, "");
  const apiUrl = `${baseAPIUrl}/api/v1/${apiUri}`;

  // Auth for BOTH staff (MSAL) and external email-OTP users — reads the unified
  // in-memory token store the generated api client uses. See getRequestAuthToken.
  const accessToken = await getRequestAuthToken();

  // Append query parameters to the API URI
  const queryString = new URLSearchParams(queryParams || {}).toString();
  const apiUriWithQuery = queryString ? `${apiUrl}?${queryString}` : apiUrl;

  // Set up headers
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${accessToken}`);

  // Check if bodyParams is a FormData instance
  let requestBody: FormData | string | null = null;
  if (bodyParams) {
    if (bodyParams instanceof FormData) {
      // Do not set 'Content-Type' manually; browser will handle it.
      requestBody = bodyParams;
    } else {
      headers.append("Content-Type", "application/json");
      requestBody = JSON.stringify(bodyParams);
    }
  }

  // Configure fetch options
  const options = {
    method: method,
    headers: headers,
    body: requestBody,
  };

  const response = await fetch(apiUriWithQuery, options);

  // Handle 401 Unauthorized - retry with a fresh token
  if (response.status === 401) {
    authDebug("callApi: received 401, attempting token refresh");

    try {
      // Refresh + re-read from the unified token store. refreshTokenIfNeeded
      // refreshes the MSAL token for staff and is a no-op for external
      // app-session tokens; getAccessTokenCookie returns whichever applies.
      await refreshTokenIfNeeded();
      const freshToken = getAccessTokenCookie();
      if (!freshToken) {
        authDebug("callApi: no token after refresh; logging out");
        await logoutUser(true);
        throw new Error("Authentication failed. Please sign in again.");
      }

      // Retry the request with the fresh token
      headers.set("Authorization", `Bearer ${freshToken}`);
      const retryResponse = await fetch(apiUriWithQuery, {
        ...options,
        headers,
      });

      if (retryResponse.status === 401) {
        authDebug("callApi: still 401 after refresh; logging out");
        await logoutUser(true);
        throw new Error("Authentication failed. Please sign in again.");
      }

      if (!retryResponse.ok) {
        throw await buildApiError(retryResponse);
      }

      const responseData = await retryResponse.json();
      return responseData;
    } catch (retryError) {
      authDebugError(
        "callApi: error refreshing token or retrying request",
        retryError
      );
      throw retryError;
    }
  }

  if (!response.ok) {
    throw await buildApiError(response);
  }

  const responseData = await response.json();
  return responseData;
}
